/** @jsxImportSource @emotion/react */
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormikErrors, useFormik } from 'formik';
import {
  AddressLookupInline,
  ActivationExitDialog,
  NavButtons,
} from 'components';
// import { ProgressBar } from 'components/sign-up'; // MC-4775 Uncomment after no longer hardcoded
import { Constants } from 'global';
import { useMerchantApplicationContext } from 'contexts';
import { StepComponentProps } from 'types/interfaces/sign-up/MerchantApplication.types';
import type { Address } from 'types';
import {
  ActivationStepKey,
  // activationStepsConfig, // MC-4775 Uncomment after no longer hardcoded
} from 'modules/activation/ActivationConfig';
import {
  getInitialActivationStepData,
  getLocationDetailsAIBannerState,
} from 'modules/activation/steps/activationStepData';
import type { ActivationLocationDetailsProps } from 'modules/activation/steps/activationStepData';
import type { StoredActivationData } from 'modules/activation/activationStorage';

const hasValue = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return value !== null && value !== undefined;
};

const formatActivationAddress = (
  address?: ActivationLocationDetailsProps['businessAddress']
): string => {
  if (!address) {
    return '';
  }
  return [
    address.unitNumber,
    [address.streetNumber, address.streetName].filter(hasValue).join(' '),
    address.suburb,
    address.state,
    address.postcode,
  ]
    .filter(hasValue)
    .join(', ');
};
import type { MerchantApplicationResponse } from 'types/responses/activation';

import { ActivationRadioGroup } from '../ActivationRadioGroup';
import * as styles from 'modules/activation/ActivationFlow.styles';

type BusinessAddress = NonNullable<
  ActivationLocationDetailsProps['businessAddress']
>;

const emptyBusinessAddress: BusinessAddress = {
  unitNumber: '',
  streetNumber: '',
  streetName: '',
  suburb: '',
  postcode: '',
  state: '',
};

const emptyValues: ActivationLocationDetailsProps = {
  businessAddress: emptyBusinessAddress,
  businessAddressDisplay: undefined,
  isPartOfFranchise: undefined,
  hasMultipleLocations: undefined,
};

const requiredBusinessAddressFields = [
  'streetNumber',
  'streetName',
  'suburb',
  'postcode',
  'state',
] as const;

const hasCompleteBusinessAddress = (
  address?: ActivationLocationDetailsProps['businessAddress']
): boolean =>
  requiredBusinessAddressFields.every((field) => hasValue(address?.[field]));

const markTouched = (
  values: ActivationLocationDetailsProps
): Record<string, any> => ({
  businessAddressDisplay: true,
  businessAddress: Object.keys(values.businessAddress ?? {}).reduce(
    (acc, key) => ({ ...acc, [key]: true }),
    {}
  ),
  isPartOfFranchise: true,
  hasMultipleLocations: true,
});

const validateValues = (
  values: ActivationLocationDetailsProps
): FormikErrors<ActivationLocationDetailsProps> => {
  const errors: FormikErrors<ActivationLocationDetailsProps> = {};
  const addressErrors: Record<string, string> = {};
  const address = values.businessAddress ?? {};

  if (!values.businessAddressDisplay?.trim()) {
    errors.businessAddressDisplay = 'Please enter your business address';
  }

  if (!address.streetNumber?.trim()) {
    addressErrors.streetNumber = 'Please select a valid business address';
  }
  if (!address.streetName?.trim()) {
    addressErrors.streetName = 'Please select a valid business address';
  }
  if (!address.suburb?.trim()) {
    addressErrors.suburb = 'Please select a valid business address';
  }
  if (!address.postcode?.trim()) {
    addressErrors.postcode = 'Please select a valid business address';
  } else if (!/^[0-9]{4}$/i.test(address.postcode)) {
    addressErrors.postcode = 'Please enter a valid postcode';
  }
  if (!address.state?.trim()) {
    addressErrors.state = 'Please select a valid business address';
  }

  if (Object.keys(addressErrors).length) {
    errors.businessAddress = addressErrors as any;
  }

  if (values.hasMultipleLocations === undefined) {
    errors.hasMultipleLocations = 'Please select an option';
  }

  if (values.isPartOfFranchise === undefined) {
    errors.isPartOfFranchise = 'Please select an option';
  }

  return errors;
};

const getAddressHelperText = (
  errors: FormikErrors<ActivationLocationDetailsProps>
): string | undefined => {
  if (typeof errors.businessAddressDisplay === 'string') {
    return errors.businessAddressDisplay;
  }

  if (errors.businessAddress && typeof errors.businessAddress === 'object') {
    return Object.values(errors.businessAddress).find(
      (error): error is string => typeof error === 'string'
    );
  }

  return undefined;
};

const buildInitialValues = (
  activationData: MerchantApplicationResponse | null,
  savedValues?: Partial<ActivationLocationDetailsProps>
): ActivationLocationDetailsProps => {
  const storedValues = getInitialActivationStepData(
    ActivationStepKey.LocationDetails,
    activationData
  ) as Partial<ActivationLocationDetailsProps>;
  const businessAddress = {
    ...emptyBusinessAddress,
    ...(storedValues.businessAddress ?? {}),
    ...(savedValues?.businessAddress ?? {}),
  };
  const businessAddressDisplay =
    savedValues?.businessAddressDisplay ??
    storedValues.businessAddressDisplay ??
    (Object.values(businessAddress).some(hasValue)
      ? formatActivationAddress(businessAddress)
      : undefined);

  return {
    ...emptyValues,
    ...storedValues,
    ...(savedValues ?? {}),
    businessAddress,
    businessAddressDisplay,
  };
};

const AIBanner = ({
  title,
  subtitle,
  loading = false,
}: {
  title: string;
  subtitle?: string;
  loading?: boolean;
}): JSX.Element => (
  <div css={styles.stepOneAIBanner} role="status">
    <img
      src={
        loading
          ? Constants.assets.signUpAssets.icons.sparkleLoader
          : Constants.assets.signUpAssets.icons.aiSuggestionSmallSparkle
      }
      css={styles.stepOneAIIcon()}
      alt=""
      aria-hidden="true"
    />
    <div css={styles.stepOneBannerCopy}>
      <p className="body1" css={styles.stepOneBannerTitle}>
        {title}
      </p>
      {subtitle && (
        <p className="body4" css={styles.stepOneBannerSubtitle}>
          {subtitle}
        </p>
      )}
    </div>
  </div>
);

const LoadingSkeleton = (): JSX.Element => (
  <div css={styles.stepOneForm} aria-label="Loading business details">
    {[0, 1, 2].map((index) => (
      <div css={styles.stepOneSection} key={index}>
        <div css={styles.stepOneSkeleton(21)} />
        <div css={styles.stepOneSkeleton(108)} />
      </div>
    ))}
  </div>
);

const MINIMUM_LOADING_TIME_MS = 2000;

export const LocationDetailsStepModule: FC<StepComponentProps> = ({
  provideSubmit,
  canContinue = true,
  isSubmitting = false,
  onContinue,
  onReturnToDashboard,
}) => {
  const {
    activationData,
    activationDataLoading,
    getActivationStepData,
    setActivationStepData,
  } = useMerchantApplicationContext();
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [minimumLoadingComplete, setMinimumLoadingComplete] = useState(false);
  const [isExitConfirmationOpen, setIsExitConfirmationOpen] = useState(false);
  const [addressResolutionFailed, setAddressResolutionFailed] = useState(false);
  const savedValuesRef = useRef(
    getActivationStepData(ActivationStepKey.LocationDetails)
  );
  // Show the loading skeleton whenever the user has not yet confirmed Step 1a —
  // at that point we are waiting for AI prefill data and should not flash stale
  // saved values. Once confirmed, the step loads instantly from saved data.
  const aiConfirmed = Boolean(
    (activationData as StoredActivationData)?.businessDetailsStepAAIConfirmed
  );

  const initialValues = useMemo(
    () => buildInitialValues(activationData, savedValuesRef.current),
    [activationData]
  );

  const formik = useFormik<ActivationLocationDetailsProps>({
    initialValues,
    enableReinitialize: true,
    validate: validateValues,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      setActivationStepData(ActivationStepKey.LocationDetails, values, true);
    },
  });

  const isLoading =
    !aiConfirmed && (activationDataLoading || !minimumLoadingComplete);

  useEffect(() => {
    if (aiConfirmed) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setMinimumLoadingComplete(true);
    }, MINIMUM_LOADING_TIME_MS);

    return (): void => window.clearTimeout(timer);
  }, [aiConfirmed]);

  const submitCurrentValues = useCallback(async (): Promise<boolean> => {
    setSubmitAttempted(true);

    const errors = await formik.validateForm();

    if (Object.keys(errors).length > 0) {
      formik.setTouched(markTouched(formik.values), false);
      return false;
    }

    await formik.submitForm();
    return true;
  }, [formik]);

  useEffect(() => {
    if (submitAttempted) {
      formik.validateForm();
    }

    if (!isLoading) {
      setActivationStepData(
        ActivationStepKey.LocationDetails,
        formik.values,
        true
      );
    }
  }, [formik.values, isLoading, submitAttempted, setActivationStepData]);

  useEffect(() => {
    provideSubmit?.(submitCurrentValues);
  }, [provideSubmit, submitCurrentValues]);

  const handleAddressChange = useCallback(
    (_: unknown, nextAddress?: Address | null): void => {
      if (nextAddress === null) {
        // null = address resolution failed, display empty state (we only display if address is resolved by Google API)
        setAddressResolutionFailed(true);
        formik.setFieldValue('businessAddress', emptyBusinessAddress);
        formik.setFieldValue('businessAddressDisplay', '');
        return;
      }

      setAddressResolutionFailed(false);

      const businessAddress = nextAddress
        ? {
            ...emptyBusinessAddress,
            unitNumber: nextAddress.unitNumber ?? '',
            streetNumber: nextAddress.streetNumber ?? '',
            streetName: nextAddress.streetName ?? '',
            suburb: nextAddress.suburb ?? '',
            postcode: nextAddress.postcode ?? '',
            state: nextAddress.state ?? '',
          }
        : emptyBusinessAddress;

      formik.setFieldValue('businessAddress', businessAddress);
      formik.setFieldValue(
        'businessAddressDisplay',
        nextAddress ? formatActivationAddress(businessAddress) : ''
      );
    },
    [formik.setFieldValue]
  );

  const handleNext = async (): Promise<void> => {
    if (!canContinue || isSubmitting) {
      return;
    }

    const isValid = await submitCurrentValues();

    if (isValid) {
      onContinue?.();
    }
  };

  const handleReturnToDashboard = async (): Promise<void> => {
    await onReturnToDashboard?.();
  };

  const bannerState = getLocationDetailsAIBannerState(activationData);
  const addressHelperText = submitAttempted
    ? getAddressHelperText(formik.errors)
    : undefined;

  // True when AI has prefilled the display string but structured fields are still empty
  // triggers auto-resolution in AddressLookupInline
  const isAIPrefillPendingResolution =
    Boolean(formik.values.businessAddressDisplay) &&
    !hasCompleteBusinessAddress(formik.values.businessAddress);

  return (
    <div css={styles.stepOneRoot}>
      {isLoading ? (
        <AIBanner
          title="Hold tight... We're searching for your details"
          loading
        />
      ) : (
        bannerState !== 'none' && (
          <AIBanner
            title={
              bannerState === 'full'
                ? "We've pre-filled your details"
                : "We've pre-filled some details"
            }
            subtitle="Please check that your details are correct"
          />
        )
      )}

      <div css={styles.stepOnePageWrapper}>
        <div css={styles.stepOnePanel}>
          <div css={styles.stepOneHeader}>
            <h1 className="heading1" css={styles.stepOneTitle}>
              Business details
            </h1>
            {/* MC-4775 Remove this hardcoded div for progressbar */}
            <div css={styles.progressTrack}>
              <div css={styles.progressFill(12)} />
            </div>
            {/* 
              MC-4775 progress bar temporarily hardcoded instead of Enum/Step based because DirectorDetails/SettlementDetails is hardcoded
              <ProgressBar
                steps={activationStepsConfig}
                activeStepKey={ActivationStepKey.LocationDetails}
              />
            */}
          </div>

          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            <form
              css={styles.stepOneForm}
              onSubmit={(event) => event.preventDefault()}
            >
              <section css={styles.stepOneSection}>
                <p className="body1" css={styles.title}>
                  Business location
                </p>
                <div className="body2" css={styles.stepOneAddressField}>
                  <AddressLookupInline
                    name="businessAddressDisplay"
                    label="Enter your business address"
                    value={formik.values.businessAddressDisplay}
                    error={
                      Boolean(addressHelperText) || addressResolutionFailed
                    }
                    helperText={
                      addressHelperText ??
                      (addressResolutionFailed
                        ? 'Please search and select your address from the dropdown'
                        : '')
                    }
                    onBlur={formik.handleBlur}
                    handleChange={handleAddressChange}
                    manualAddress={
                      isAIPrefillPendingResolution
                        ? undefined
                        : formik.values.businessAddress
                    }
                    autoResolve={isAIPrefillPendingResolution}
                  />
                </div>
              </section>

              <ActivationRadioGroup
                name="hasMultipleLocations"
                label="Do you have multiple locations?"
                value={formik.values.hasMultipleLocations}
                onChange={(value) => {
                  formik.setFieldValue('hasMultipleLocations', value);
                }}
                error={
                  submitAttempted && Boolean(formik.errors.hasMultipleLocations)
                }
                helperText={
                  submitAttempted
                    ? formik.errors.hasMultipleLocations
                    : undefined
                }
              />

              <ActivationRadioGroup
                name="isPartOfFranchise"
                label="Are you part of a franchise group?"
                value={formik.values.isPartOfFranchise}
                onChange={(value) => {
                  formik.setFieldValue('isPartOfFranchise', value);
                }}
                error={
                  submitAttempted && Boolean(formik.errors.isPartOfFranchise)
                }
                helperText={
                  submitAttempted ? formik.errors.isPartOfFranchise : undefined
                }
              />

              <NavButtons
                canGoBack
                canContinue={canContinue}
                isSubmitting={isSubmitting}
                onBack={() => setIsExitConfirmationOpen(true)}
                onContinue={handleNext}
                backLabel="Return to dashboard"
                continueLabel="Next"
              />
            </form>
          )}
        </div>
      </div>

      <ActivationExitDialog
        open={isExitConfirmationOpen}
        onCancelExit={() => setIsExitConfirmationOpen(false)}
        onConfirmExit={handleReturnToDashboard}
      />
    </div>
  );
};

export default LocationDetailsStepModule;
